import { all, call, fork, put, take, takeEvery, takeLatest, select, getContext, delay } from 'redux-saga/effects';
import { AuthActionTypes, AuthState, UserRole } from './types';
import * as actions from './actions';
import { UIActionTypes } from '../ui/types';
import { changeHeaderTab } from '../ui/actions';
import { requestFilters, handleOpenedStudentRecords } from '../dashboard/actions';
import { getAuth, getIntakeId, getConfig as getUniConfig, getStudent } from '../selectors';
import { logout, setUsersIntake } from './actions';
import {
  acceptTerms,
  getUniUser,
  getImmigrationProviderUser,
  getPartnerUser,
  getAgentUser,
  getEnrolyUser,
  getAdminUser,
  getAgency,
} from '../../api/api';
import { IIntakeId, IntakeStatus, CHANNEL, TOPIC } from 'types';
import { ApplicationState } from '../index';
import { filterIntakesByAllowedStatuses } from '../../utilities/filterIntakesByAllowedStatuses';
import { CAS_PROCESS_INTAKE_STATUSES } from '../../utilities/constants';
import { safeLocalStorageSet } from '../../utilities/localStorage';
import { DashboardActionTypes } from '../dashboard/types';
import { getMainSaga, websocketActions } from 'websocket-utility';
import { IConfigState } from '../../types';
import { getWSSUrl } from '../../utilities/envVariables';
import { sagaMiddleware } from '../configureStore';
import { StudentState } from '../student/types';
import { studentBaseUrl } from '../../App';

function* registerWithLogRocket() {
  const w: any = window;
  if (process.env.REACT_APP_ENV !== 'standalone' && !w.location.hostname.includes('.net') && w.LogRocket) {
    try {
      const context = yield getContext('sagaContext');
      const idToken: any = yield call(context.getIdToken);
      if (!idToken) {
        yield put(logout());
        yield call(context.onLogout);
        console.log('Refresh Token has Expired - registerWithLogRocket');
        return;
      }
      const userName: string = context.username;

      yield w.LogRocket.identify(userName, {
        email: context.email,
        uni: context.uni,
      });

      if (w.Appcues) {
        const cognitoGroups = context.groups ?? [];
        const cognitoGroup = cognitoGroups.find((groupName: string) => groupName !== 'Insights') || 'Uni';

        yield w.Appcues.identify(userName, {
          email: context.email,
          uni: context.uni,
          role: cognitoGroup,
        });
      }
    } catch (e) {
      console.log(e);
    }
  }
}

function* handleLogout() {
  console.log('logout called!');
  const auth = yield select(getAuth);
  if (auth.isLoggedIn) {
    console.log('logout called! 2');
    yield put(actions.logoutSuccess());
  }
}

function* handleChangeIntake() {
  yield put(requestFilters());
  // Re-subscribe to WebSocket with the new intake
  // Only do this if we're on the dashboard (not viewing a specific student)
  if (!window.location.pathname.startsWith(studentBaseUrl)) {
    const uniConfig: IConfigState = yield select(getUniConfig);
    const newIntakeId = yield select(getIntakeId);
    const viewUni = uniConfig.university_code;

    const studentListSubKey = {
      channel: CHANNEL.STUDENT_LIST,
      view: `${viewUni}#${newIntakeId}`,
      topic: [TOPIC.OPEN_STUDENT],
    };

    // Clear any existing opened student records when changing intake
    yield put({ type: DashboardActionTypes.CLEAR_OPENED_RECORDS });

    // Subscribe to the new view (the websocket-utility will handle duplicate subscriptions)
    yield put(
      websocketActions.subscribeToWebsocket({
        key: JSON.stringify(studentListSubKey),
        handler: handleOpenedStudentRecords,
      }),
    );
  }
}

function* handleTermsAccept() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);

    const userName = context.username;
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleTermsAccept');
      return;
    }
    yield call(acceptTerms, idToken, userName);
    yield put(actions.acceptTermsSuccess());
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.acceptTermsFailure(err.message));
    } else {
      yield put(actions.acceptTermsFailure('An unknown error occurred.'));
    }
  }
}

function* handleGetCurrentUser() {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetCurrentUser');
      return;
    }

    const { uni } = context;
    const { role, student_number: username, agencyId } = yield select(getAuth);

    let response: any;
    switch (role) {
      case UserRole.Uni:
      case UserRole.UniAdmin:
      case UserRole.UniLite:
      case UserRole.UniSecure:
        response = yield call(getUniUser, idToken, uni, username);
        break;
      case UserRole.ImmigrationProvider:
        response = yield call(getImmigrationProviderUser, idToken, username);
        break;
      case UserRole.Partner:
        response = yield call(getPartnerUser, idToken, username);
        break;
      case UserRole.AgencyAdmin:
      case UserRole.Agent:
        response = yield call(getAgentUser, idToken, username);
        // Get the agent's agency data if agencyId is available
        if (agencyId) {
          yield put(actions.getAuthAgencyRequest(agencyId, true));
        }
        break;
      case UserRole.Enroly:
        response = yield call(getEnrolyUser, idToken, username);
        break;
      default:
        response = yield call(getAdminUser, idToken, username);
        break;
    }

    if (response) {
      yield put(actions.getCurrentUserSuccess(response));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getCurrentUserFailure(err.message));
    } else {
      yield put(actions.getCurrentUserFailure('An unknown error occurred.'));
    }
  }
}

function* handleHeaderTabChange(action: ReturnType<typeof changeHeaderTab>) {
  const tab = action.payload;
  const intakeId = yield select(({ auth }: ApplicationState) => auth.intake);
  const intakeIds: IIntakeId[] = yield select(({ config }: ApplicationState) => config.intakeIds);
  const casProcessIntakes = filterIntakesByAllowedStatuses(intakeIds, CAS_PROCESS_INTAKE_STATUSES);
  const currentIntake: IIntakeId = intakeIds.find(intake => intake.value === intakeId) ?? intakeIds[0];
  /* Not Invited section has access to future intakes that are hidden on CAS Process.
   * And if future intake was selected on Not Invited, when switching back to CAS Process intake is set to
   * first intake from allowed for CAS Process */
  if (tab === 'casProcess' && currentIntake.status === IntakeStatus.FUTURE) {
    const newIntakeId = casProcessIntakes[0].value;
    yield put(setUsersIntake(newIntakeId));
    safeLocalStorageSet('dashboard-intake-filter-value', newIntakeId);
  }
}

function* handleRegisterInitialWebsockets(): any {
  const auth: AuthState = yield select(getAuth);
  let student: StudentState | undefined;

  if (window.location.pathname.startsWith(studentBaseUrl)) {
    student = yield select(getStudent);
    if (!student?.core?.student_id) {
      yield take('@@student/GET_STUDENT_RECORD_SUCCESS');
      student = yield select(getStudent);
    }
  } else {
    const allStudentsLoaded: boolean = yield select(({ dashboard }: ApplicationState) => dashboard.allStudentsLoaded);
    if (!allStudentsLoaded) {
      yield take(DashboardActionTypes.GET_STUDENTS_SUCCESS);
    }
  }
  yield delay(1000);
  const uniConfig: IConfigState = yield select(getUniConfig);

  // When on student page, use the student's intake and university to match the openStudent message
  // Otherwise use the dashboard's selected intake
  const intakeId = student?.summary?.intakeId || (yield select(getIntakeId));

  let viewUni: string;
  if (student) {
    viewUni = auth.isPathway ? auth.uni : student.core.university;
  } else {
    viewUni = uniConfig.university_code;
  }

  const studentListSubKey = {
    channel: CHANNEL.STUDENT_LIST,
    view: `${viewUni}#${intakeId}`,
    topic: [TOPIC.OPEN_STUDENT],
  };

  yield put(
    websocketActions.subscribeToWebsocket({
      key: JSON.stringify(studentListSubKey),
      handler: handleOpenedStudentRecords,
    }),
  );
}

function* watchLogoutRequest() {
  yield takeLatest(AuthActionTypes.LOGOUT_REQUEST, handleLogout);
}

function* handleRegisterWebsocketSaga() {
  const context = yield getContext('sagaContext');
  const idToken: any = yield call(context.getIdToken);
  const wssUrl: string = yield call(getWSSUrl);
  // const authData = awsApi.getAuthData();

  const auth = yield select(getAuth);
  if (auth.isLoggedIn) {
    // calling HOC to async inject into middleware
    const websocketSaga = getMainSaga(wssUrl, idToken!);
    sagaMiddleware.run(websocketSaga);
  }
}

function* watchSetSagaContext() {
  yield takeEvery(UIActionTypes.SET_CONTEXT_SUCCESS, registerWithLogRocket);
  yield takeEvery(UIActionTypes.SET_CONTEXT_SUCCESS, handleGetCurrentUser);
  yield takeEvery(UIActionTypes.SET_CONTEXT_SUCCESS, handleRegisterWebsocketSaga);
}

function* watchIntakeChange() {
  yield takeLatest(AuthActionTypes.SET_USERS_INTAKE, handleChangeIntake);
}

function* watchTermsAccept() {
  yield takeEvery(AuthActionTypes.ACCEPT_TERMS, handleTermsAccept);
}

function* watchWSConnect() {
  yield takeEvery('CONNECTION_SUCCESS', handleRegisterInitialWebsockets);
}

function* handleGetAuthAgency(action: ReturnType<typeof actions.getAuthAgencyRequest>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetAuthAgency');
      return;
    }

    const { agencyId, checkApplyAccess } = action.payload;
    if (!agencyId) {
      yield put(actions.getAuthAgencyFailure('No agency ID provided'));
      return;
    }

    const response = yield call(getAgency, idToken, agencyId, checkApplyAccess);
    if (response) {
      yield put(actions.getAuthAgencySuccess(response));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getAuthAgencyFailure(err.message));
    } else {
      yield put(actions.getAuthAgencyFailure('An unknown error occurred.'));
    }
  }
}

function* watchGetAuthAgency() {
  yield takeLatest(AuthActionTypes.GET_AUTH_AGENCY_REQUEST, handleGetAuthAgency);
}

function* AuthSaga() {
  yield all([fork(watchLogoutRequest)]);
  yield all([fork(watchIntakeChange)]);
  yield all([fork(watchTermsAccept)]);
  yield all([takeEvery(UIActionTypes.SET_HEADER_TAB, handleHeaderTabChange)]);
  yield all([fork(watchWSConnect)]);
  yield all([fork(watchSetSagaContext)]);
  yield all([fork(watchGetAuthAgency)]);
}

export default AuthSaga;
